import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { getAIResponse, getQuickPrompts, analyzeCanvasState } from '../../../services/funnelAIService';

export default function FunnelAIAssistant({ nodes, edges, isOpen, onToggle }) {
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: 'Hi! I\'m your funnel building assistant. I can help you create effective advertising campaigns. What would you like to know?'
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const quickPrompts = getQuickPrompts(nodes, edges);
    const suggestions = analyzeCanvasState(nodes, edges);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        if (!inputValue.trim() || isLoading) return;

        const userMessage = inputValue.trim();
        setInputValue('');
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
        }
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsLoading(true);

        try {
            const response = await getAIResponse(userMessage, { nodes, edges });
            setMessages(prev => [...prev, { role: 'assistant', content: response }]);
        } catch (error) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickPrompt = (prompt) => {
        setInputValue(prompt);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Collapsed state
    if (!isOpen) {
        return (
            <button
                onClick={onToggle}
                style={{
                    position: 'fixed',
                    bottom: 'var(--space-4)',
                    right: '340px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-3) var(--space-4)',
                    backgroundColor: 'var(--color-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-full)',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 99,
                    fontSize: '0.9rem',
                    fontWeight: 500
                }}
            >
                <MessageCircle size={18} />
                AI Assistant
                {suggestions.length > 0 && (
                    <span style={{
                        backgroundColor: 'white',
                        color: 'var(--color-primary)',
                        borderRadius: 'var(--radius-full)',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700
                    }}>
                        {suggestions.length}
                    </span>
                )}
            </button>
        );
    }

    return (
        <div style={{
            position: 'fixed',
            bottom: 'var(--space-4)',
            right: '340px',
            width: '380px',
            height: '500px',
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            border: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 99,
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'var(--color-primary)',
                color: 'white'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Sparkles size={18} />
                    <span style={{ fontWeight: 600 }}>AI Assistant</span>
                </div>
                <button
                    onClick={onToggle}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 'var(--space-1)',
                        color: 'white'
                    }}
                >
                    <ChevronDown size={18} />
                </button>
            </div>

            {/* Suggestions bar */}
            {suggestions.length > 0 && (
                <div style={{
                    padding: 'var(--space-2) var(--space-3)',
                    backgroundColor: 'var(--warning-50)',
                    borderBottom: '1px solid var(--color-border)',
                    fontSize: '0.8rem'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span style={{ color: 'var(--warning-700)', fontWeight: 500 }}>
                            Suggestion:
                        </span>
                        <span style={{ color: 'var(--warning-800)' }}>
                            {suggestions[0].message}
                        </span>
                    </div>
                </div>
            )}

            {/* Messages */}
            <div style={{
                flex: 1,
                overflow: 'auto',
                padding: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)'
            }}>
                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        style={{
                            display: 'flex',
                            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                        }}
                    >
                        <div style={{
                            maxWidth: '85%',
                            padding: 'var(--space-3)',
                            borderRadius: 'var(--radius-lg)',
                            backgroundColor: msg.role === 'user'
                                ? 'var(--color-primary)'
                                : 'var(--color-bg-secondary)',
                            color: msg.role === 'user'
                                ? 'white'
                                : 'var(--color-text-primary)',
                            fontSize: '0.85rem',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap'
                        }}>
                            {msg.content}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-start'
                    }}>
                        <div style={{
                            padding: 'var(--space-3)',
                            borderRadius: 'var(--radius-lg)',
                            backgroundColor: 'var(--color-bg-secondary)',
                            color: 'var(--color-text-tertiary)',
                            fontSize: '0.85rem'
                        }}>
                            Thinking...
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Quick prompts */}
            <div style={{
                padding: 'var(--space-2) var(--space-3)',
                borderTop: '1px solid var(--color-border)',
                display: 'flex',
                gap: 'var(--space-2)',
                flexWrap: 'wrap'
            }}>
                {quickPrompts.slice(0, 2).map((prompt, idx) => (
                    <button
                        key={idx}
                        onClick={() => handleQuickPrompt(prompt)}
                        style={{
                            padding: 'var(--space-1) var(--space-2)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-full)',
                            backgroundColor: 'transparent',
                            color: 'var(--color-text-secondary)',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {prompt}
                    </button>
                ))}
            </div>

            {/* Input */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                padding: 'var(--space-3)',
                borderTop: '1px solid var(--color-border)'
            }}>
                <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        // Auto-resize textarea
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                    }}
                    onKeyDown={handleKeyPress}
                    placeholder="Ask about funnel building... (Enter to send, Shift+Enter for new line)"
                    rows={1}
                    style={{
                        flex: 1,
                        padding: 'var(--space-2) var(--space-3)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: 'var(--color-surface)',
                        color: 'var(--color-text-primary)',
                        fontSize: '0.9rem',
                        resize: 'none',
                        minHeight: '38px',
                        maxHeight: '150px',
                        lineHeight: '1.5',
                        overflowY: 'auto'
                    }}
                />
                <button
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isLoading}
                    style={{
                        padding: 'var(--space-2)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: inputValue.trim() ? 'var(--color-primary)' : 'var(--color-border)',
                        color: inputValue.trim() ? 'white' : 'var(--color-text-tertiary)',
                        cursor: inputValue.trim() ? 'pointer' : 'not-allowed'
                    }}
                >
                    <Send size={18} />
                </button>
            </div>
        </div>
    );
}
